/* Repository path: frontend/src/app/router.ts */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Route } from "../types";

/**
 * W4-05: the item-detail fragment. Matches `#detail/<item_code>` (a
 * leading `#` optional, since callers pass both `window.location.hash`,
 * which includes it, and a bare hash value). The code itself must satisfy
 * the catalogue's own pattern — lowercase snake_case, at least two
 * segments — and stay under a sane length, since the fragment is
 * untrusted input read directly off the URL. A hash that doesn't match
 * (wrong shape, empty, unrelated, or too long) yields no `itemCode` at
 * all: the plan renders as normal rather than guessing. See
 * docs/architecture/adr/ADR-0009-client-route-privacy-and-item-detail-addressing.md.
 */
const ITEM_DETAIL_HASH = /^#?detail\/([a-z][a-z0-9]*(?:_[a-z0-9]+)+)$/;
const MAX_ITEM_CODE_LENGTH = 128;

function itemCodeFromHash(hash: string): string | undefined {
  const match = ITEM_DETAIL_HASH.exec(hash);
  const code = match?.[1];
  if (!code || code.length > MAX_ITEM_CODE_LENGTH) return undefined;
  return code;
}

export function matchRoute(pathname: string, hash = ""): Route {
  if (pathname === "/" || pathname === "/welcome") return { name: "welcome" };
  const question = pathname.match(/^\/questionnaire\/(1|2|3|4|5)\/?$/);
  if (question) return { name: "questionnaire", step: Number(question[1]) as 1 | 2 | 3 | 4 | 5 };
  if (/^\/unsupported\/?$/.test(pathname)) return { name: "welcome" };
  if (/^\/result\/?$/.test(pathname)) return { name: "result", itemCode: itemCodeFromHash(hash) };
  if (/^\/result\/refine\/?$/.test(pathname)) return { name: "refine-result" };
  if (/^\/result\/edit-profile\/?$/.test(pathname)) return { name: "edit-profile-result" };
  if (/^\/access\/verify\/?$/.test(pathname)) return { name: "verify" };
  if (/^\/access\/recovery\/?$/.test(pathname)) return { name: "recovery" };
  if (/^\/reminders\/off\/?$/.test(pathname)) return { name: "reminders-off" };
  if (/^\/privacy\/?$/.test(pathname)) return { name: "privacy" };
  const plan = pathname.match(/^\/plans\/([^/]+)\/?$/);
  if (plan) return { name: "plan", publicId: plan[1], itemCode: itemCodeFromHash(hash) };
  const planRefine = pathname.match(/^\/plans\/([^/]+)\/refine\/?$/);
  if (planRefine) return { name: "refine-plan", publicId: planRefine[1] };
  const planEditProfile = pathname.match(/^\/plans\/([^/]+)\/edit-profile\/?$/);
  if (planEditProfile) return { name: "edit-profile-plan", publicId: planEditProfile[1] };
  return { name: "not-found" };
}

export function useRouter() {
  const [route, setRoute] = useState(() =>
    matchRoute(window.location.pathname, window.location.hash),
  );
  // W4-05: which item detail was open just before the most recent route
  // change, if any — read once by the plan page to return focus to that
  // item's card, then cleared via `clearJustClosedItemCode`. Detail is
  // opened and closed by plain `<a href="#detail/...">` / `<a
  // href={pathname}>` links (native fragment navigation, verified against
  // jsdom and real browsers alike — no imperative "open detail" function
  // is needed anywhere), so this hook has to notice the transition itself
  // rather than being told about it. See
  // docs/design/features/W4-05.md "Responsive/accessibility".
  const previousItemCode = useRef<string | undefined>(route.itemCode);
  const [justClosedItemCode, setJustClosedItemCode] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handleChange = () => {
      const next = matchRoute(window.location.pathname, window.location.hash);
      if (previousItemCode.current && !next.itemCode) {
        setJustClosedItemCode(previousItemCode.current);
      } else if (next.itemCode) {
        setJustClosedItemCode(undefined);
      }
      previousItemCode.current = next.itemCode;
      setRoute(next);
    };
    window.addEventListener("popstate", handleChange);
    window.addEventListener("hashchange", handleChange);
    return () => {
      window.removeEventListener("popstate", handleChange);
      window.removeEventListener("hashchange", handleChange);
    };
  }, []);

  const navigate = useCallback((path: string, replace = false) => {
    window.history[replace ? "replaceState" : "pushState"]({}, "", path);
    previousItemCode.current = undefined;
    setJustClosedItemCode(undefined);
    setRoute(matchRoute(path, ""));
    document.querySelector<HTMLElement>("#main-content")?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }, []);

  const clearJustClosedItemCode = useCallback(() => setJustClosedItemCode(undefined), []);

  return { route, navigate, justClosedItemCode, clearJustClosedItemCode };
}



